/**
 * Stage B UI — thin dialog wrapper for useEnrollPatientInTrial.
 * Mount wherever a patient row offers a "Enroll in trial" action.
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useEnrollPatientInTrial } from './useEnrollPatientInTrial';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  patientProfileId: string;
  trialSiteId: string;
  trialId: string;
  croTrialId?: string;
  onEnrolled?: (assignmentId: string) => void;
}

export function EnrollPatientDialog({
  open, onOpenChange, patientProfileId, trialSiteId, trialId, croTrialId, onEnrolled,
}: Props) {
  const [baseline, setBaseline] = useState(new Date().toISOString().slice(0, 10));
  const [randomise, setRandomise] = useState(false);
  const enroll = useEnrollPatientInTrial();

  const handleEnroll = async () => {
    try {
      const res = await enroll.mutateAsync({
        patientProfileId, trialSiteId, trialId, croTrialId,
        baselineDate: baseline, randomise,
        workflowStage: randomise ? 'randomized' : 'enrolled',
      });
      toast.success(
        `Patient is now a Participant. ${res.patientVisitsCreated} visit${res.patientVisitsCreated === 1 ? '' : 's'} scheduled${res.randomized ? ` · arm: ${res.arm ?? 'assigned'}` : ''}.`,
      );
      onEnrolled?.(res.assignmentId);
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enrollment failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Enroll patient in trial</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="baseline">Baseline (Day 0) date</Label>
            <Input id="baseline" type="date" value={baseline} onChange={(e) => setBaseline(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              Visits will be scheduled from this date using the protocol's day offsets.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="rand" checked={randomise} onCheckedChange={(v) => setRandomise(v === true)} />
            <Label htmlFor="rand" className="cursor-pointer">Randomise on enrollment</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleEnroll} disabled={enroll.isPending}>
            {enroll.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Enroll
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
